import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Briefcase, Plus, FileText, Trash2, Upload, RefreshCw, Download, Copy, Eye, Layers, History, Award, ChevronDown, Pencil, AlertTriangle, ClipboardCopy, ClipboardPaste, NotepadText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";
import { copyPart, getClipboardPart } from "@/utils/partClipboard";
import MiscFilesPanel from "@/components/MiscFilesPanel";
import ProjectCompareView from "@/components/ProjectCompareView";
import ProjectSourcingRollup from "@/components/ProjectSourcingRollup";
import ProjectPartBreakdown from "@/components/ProjectPartBreakdown";
import QuotePdfDownloader from "@/components/QuotePdfDownloader";
import AssemblyPdfDownloader from "@/components/AssemblyPdfDownloader";

// Display labels for machine-requirement types (from the operation's machine_type).
const MACHINE_TYPE_DISPLAY = { press: "Press", cnc: "CNC", vmc: "VMC", welding: "Welding", other: "Other", cnc_vmc: "CNC/VMC" };

// Module-level restore-window guard (survives React StrictMode's dev double-mount).
let projectDetailRestoreUntil = 0;

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, hasPermission } = useAuth();
  const canDeletePart = hasPermission("npd.delete");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newPart, setNewPart] = useState({ part_name: "", part_number: "" });
  const [clip, setClip] = useState(getClipboardPart());  // copy/paste part clipboard
  const [pasting, setPasting] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [comparingRound, setComparingRound] = useState(null);
  const [savingRound, setSavingRound] = useState(false);
  const [outcomeMenuOpen, setOutcomeMenuOpen] = useState(false);
  const [pdfNpd, setPdfNpd] = useState(null);  // in-page quote → PDF (no new tab)
  const [pdfAsmId, setPdfAsmId] = useState(null);  // in-page assembly quote → PDF
  const [priceModal, setPriceModal] = useState(null);  // "target" | "settled" — customer price negotiation modal
  const [exportingXlsx, setExportingXlsx] = useState(false);  // Export Excel in progress
  const [exportingZip, setExportingZip] = useState(false);    // All Quotes (ZIP) in progress

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${id}`);
      setProject(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Linked project was deleted or could not be loaded");
      nav("/npd");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadRounds(); /* eslint-disable-next-line */ }, [id]);

  // Round 32 — load customers list for clone modal lookup (case-insensitive
  // exists-check + source-customer name resolution).
  useEffect(() => {
    api.get("/customers").then((r) => setCustomersForClone(r.data || [])).catch(() => {});
  }, []);

  // Scroll persistence across navigate-away/back (sessionStorage, project-scoped).
  // Mirrors NpdList.jsx: a rAF loop re-applies the saved scroll each frame within a
  // restore window, guarded by a module-level flag so StrictMode's double-mount can't
  // flip it off mid-restore. Save-on-scroll ignores the browser's nav-away reset-to-top.
  const SS_KEY = `projectdetail:scroll:${id}`;
  useEffect(() => {
    if (loading) return;
    let y = 0;
    try { y = Number(sessionStorage.getItem(SS_KEY)) || 0; } catch { /* ignore */ }
    if (!y) return;
    projectDetailRestoreUntil = Date.now() + 1800;
    let raf;
    const tick = () => {
      if (window.scrollY !== y) window.scrollTo(0, y);
      if (Date.now() > projectDetailRestoreUntil) return;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line
  }, [loading]);
  useEffect(() => {
    let raf = null;
    let prevY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const navReset = y === 0 && prevY > 150;
      prevY = y;
      if (Date.now() < projectDetailRestoreUntil || navReset) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        try { sessionStorage.setItem(SS_KEY, String(y)); } catch { /* ignore */ }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line
  }, [id]);

  const addPart = async () => {
    if (!newPart.part_name || !newPart.part_number) {
      toast.error("Part name and number are required.");
      return;
    }
    setAdding(true);
    try {
      const r = await api.post(`/projects/${id}/parts`, {
        part_name: newPart.part_name,
        part_number: newPart.part_number,
        customer_id: project.customer_id,
      });
      toast.success(`Added ${r.data.npd_number}`);
      setNewPart({ part_name: "", part_number: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add part failed");
    } finally {
      setAdding(false);
    }
  };

  // Round 21.5 — add child assembly to project
  const addAssembly = async () => {
    const name = window.prompt("Assembly name (optional):") || "";
    try {
      const r = await api.post(`/projects/${id}/assemblies`, { name });
      toast.success(`Created ${r.data.assembly_number}`);
      nav(`/assemblies/${r.data.assembly_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add assembly failed");
    }
  };

  // Round 21.6 — clone a project-child NPD as another child of the same project
  const clonePart = async (c) => {
    if (!window.confirm(
      `Clone part "${c.part_name || c.npd_number}" as a new part in this project?\n\n` +
      `Drawing + approval + negotiation rounds will be reset on the clone. Edit name and part number after.`
    )) return;
    try {
      const r = await api.post(`/projects/${id}/parts/${c.npd_id}/clone`);
      toast.success(`Cloned as ${r.data.npd_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  // Move a part OUT of this project so it becomes a standalone single costing. The costing
  // itself is untouched — it just gets a fresh NPD number and leaves the project.
  const convertToSingle = async (c) => {
    if (!window.confirm(
      `Make "${c.part_name || c.npd_number}" a single costing?\n\n` +
      `It LEAVES this project and gets a new standalone NPD number. All costing data, rounds ` +
      `and attachments stay with it, and the remaining parts are renumbered P1, P2, …\n\n` +
      `To bring it back into a project later, use the copy / "Paste part" buttons.`
    )) return;
    try {
      const r = await api.post(`/projects/${id}/parts/${c.npd_id}/convert-to-single`);
      toast.success(`${r.data.previous_npd_number} → ${r.data.npd_number} (now a single costing)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
  };

  // Copy a part / assembly to the cross-page clipboard (paste into any project later).
  const copyToClipboard = (kind, cid, label) => {
    copyPart(kind, cid, label);
    setClip(getClipboardPart());
    toast.success(`Copied "${label}". Open any project and click "Paste part".`);
  };

  // Paste the clipboard part/assembly into THIS project as a new editable child.
  const pastePart = async () => {
    const c = getClipboardPart();
    if (!c) { toast.error("Nothing copied yet. Click the copy icon on a part first."); return; }
    setPasting(true);
    try {
      const r = await api.post(`/projects/${id}/paste-part`, { kind: c.kind, source_id: c.id });
      toast.success(`Pasted as ${r.data.npd_number || r.data.assembly_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paste failed");
    } finally {
      setPasting(false);
    }
  };

  // Soft-delete a part. Server enforces the npd.delete permission (RBAC); the UI
  // button is also hidden without it. Deleted parts move to Trash (recoverable by admin).
  const deletePart = async (c) => {
    if (!window.confirm(
      `Delete part "${c.part_name || c.npd_number}" from this project?\n\n` +
      `It will be moved to Trash (recoverable by an admin). The part's cost is removed from the project total.`
    )) return;
    try {
      await api.delete(`/npd/${c.npd_id}`);
      toast.success(`Deleted ${c.npd_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const cloneAssembly = async (a) => {
    if (!window.confirm(`Clone assembly "${a.assembly_name || a.assembly_number}" as a new draft?`)) return;
    try {
      const r = await api.post(`/assemblies/${a.assembly_id}/clone`);
      toast.success(`Cloned as ${r.data.assembly_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };
  const deleteAssembly = async (a) => {
    if (!window.confirm(`Delete assembly "${a.assembly_name || a.assembly_number}"?\n\nIt moves to Trash (admin-recoverable).`)) return;
    try {
      await api.delete(`/assemblies/${a.assembly_id}`);
      toast.success(`Deleted ${a.assembly_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const bulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 50) {
      toast.error("Max 50 files per upload");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append("files", f));
      const r = await api.post(`/projects/${id}/parts/bulk-upload`, fd);
      setBulkResult(r.data);
      toast.success(`Created ${r.data.created_count} parts${r.data.error_count ? ` (${r.data.error_count} failed)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const loadRounds = async () => {
    try {
      const r = await api.get(`/projects/${id}/rounds`);
      setRounds(r.data.rounds || []);
    } catch (e) {
      // silent — endpoint will 403 for unauthorized engineers
    }
  };

  const saveRound = async () => {
    const childCount = project?.children?.length || 0;
    if (childCount === 0) { toast.error("Project has no child parts to snapshot"); return; }
    const notes = window.prompt(
      `Save Round N${(project?.current_round || 0) + 1} for ${project.project_number}?\n\n` +
      `This will snapshot the project + all ${childCount} child parts at their current state. Each child's own round counter will also bump.\n\n` +
      `Notes for this round (optional):`,
      ""
    );
    if (notes === null) return;
    setSavingRound(true);
    try {
      const r = await api.post(`/projects/${id}/save-round`, { notes });
      if (r.data?.approval_revoked) {
        toast.success(`Project Round ${r.data.round_label} saved (${r.data.child_round_count} child rounds cascaded). Previous approval revoked — needs re-approval for export.`);
      } else {
        toast.success(`Saved ${r.data.round_label} (${r.data.child_round_count} child rounds cascaded)`);
      }
      // R47 — Local state update so the Approved badge clears immediately
      setProject((p) => p ? ({ ...p, approved_for_export: false, approved_by: null, approved_at: null }) : p);
      await load();
      await loadRounds();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save round failed");
    } finally { setSavingRound(false); }
  };

  const applyDefaults = async () => {
    const childCount = project.children?.length || 0;
    if (childCount === 0) {
      toast.error("Project has no child parts");
      return;
    }
    if (!window.confirm(
      `Apply current project defaults to all ${childCount} child parts?\n\n` +
      `This will OVERWRITE each child's:\n` +
      `  • Overheads table\n` +
      `  • Scrap recovery factor\n` +
      `  • Tool maintenance %\n\n` +
      `Existing material, processes, BOP, treatments, and dimensions are NOT touched. Continue?`
    )) return;
    try {
      const r = await api.post(`/projects/${id}/apply-defaults`);
      toast.success(`Applied to ${r.data.updated_count} parts`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    }
  };

  const exportExcel = async () => {
    if (exportingXlsx) return;  // ignore repeat clicks while a download is in flight
    if ((project.children?.length || 0) === 0) {
      toast.error("Project has no parts to export");
      return;
    }
    setExportingXlsx(true);
    try {
      const res = await api.get(`/projects/${id}/export.xlsx?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.project_number.replace("/", "-")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExportingXlsx(false);
    }
  };

  const downloadAllQuotes = async () => {
    if (exportingZip) return;  // ignore repeat clicks while a download is in flight
    if ((project.children?.length || 0) === 0) {
      toast.error("Project has no parts to export");
      return;
    }
    setExportingZip(true);
    try {
      const res = await api.get(`/projects/${id}/export-quotes.zip?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.project_number.replace("/", "-")}_quotes.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("All quotes (ZIP) downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setExportingZip(false);
    }
  };

  // Round 32 — clone modal on detail page. Mirrors ProjectList.jsx
  // pattern: text input for customer name, auto-creates customer in
  // master if name not found.
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneCustomerName, setCloneCustomerName] = useState("");
  const [cloneProjectName, setCloneProjectName] = useState("");
  const [cloning, setCloning] = useState(false);
  const [customersForClone, setCustomersForClone] = useState([]);
  // Round 35 — custom dropdown for clone customer combobox
  const [customerListOpen, setCustomerListOpen] = useState(false);
  const customerComboRef = useRef(null);

  // Round 35 — close customer dropdown when clicking outside
  useEffect(() => {
    if (!customerListOpen) return;
    const onDocClick = (e) => {
      if (customerComboRef.current && !customerComboRef.current.contains(e.target)) {
        setCustomerListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [customerListOpen]);

  const openCloneModal = () => {
    if (!project) return;
    const sourceCustomerName =
      (customersForClone.find((c) => c.customer_id === project.customer_id) || {}).name || "";
    setCloneCustomerName(sourceCustomerName);
    setCloneProjectName(project.project_name || "");
    setCloneModalOpen(true);
  };

  const confirmCloneProject = async () => {
    const trimmed = (cloneCustomerName || "").trim();
    const trimmedName = (cloneProjectName || "").trim();
    if (!trimmed) { toast.error("Customer name cannot be empty"); return; }
    if (!trimmedName) { toast.error("Project name cannot be empty"); return; }
    setCloning(true);
    try {
      const r = await api.post(`/projects/${id}/clone`, {
        target_customer_name: trimmed,
        target_project_name: trimmedName,
      });
      toast.success(`Cloned → ${r.data.project_number} (${r.data.children_cloned} parts)`);
      setCloneModalOpen(false);
      nav(`/projects/${r.data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally {
      setCloning(false);
    }
  };

  const deleteProject = async () => {
    if (!window.confirm(`Soft-delete ${project.project_number} and ALL its child parts?\n\nAdmin can restore from Trash.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project moved to Trash");
      nav("/npd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (!project) return null;

  const isAdmin = user?.role === "admin";
  const hideMargin = user?.role === "npd_engineer";
  const isProto = !!project?.is_prototype;
  // Per-part Development Cost when a Customer Settled Price is agreed: the settled TOTAL is
  // split EQUALLY across parts (share = total ÷ parts), and each part's Dev = share − its Grand
  // Total. So Grand Total + Dev = share per part, and summed over all parts = the settled total.
  // e.g. ₹20,00,000 ÷ 10 = ₹2,00,000 share; part with GT ₹727.27 → Dev ₹1,99,272.73.
  const settledPartDev = (c) => {
    const sheet = isProto ? project?.customer_settled_sheet : null;
    const sm = sheet?.summary;
    if (!sm) return Number(c.total_development_cost) || 0;
    // Share-equally logic applies ONLY to the "Grand Total + Tooling (one figure)" / combined
    // basis. The "separate" basis keeps each part's own raw tooling.
    if (sm.quote_basis !== "combined") return Number(c.total_development_cost) || 0;
    const settledTotal = sm.grand_total_with_dev != null
      ? Number(sm.grand_total_with_dev)
      : (Number(sm.grand_total) || 0) + (Number(sm.dev) || 0);
    const n = (project?.children || []).length || 1;
    const share = settledTotal / n;
    const gt = (c.customer_settled_price != null && c.customer_settled_price !== "")
      ? Number(c.customer_settled_price) : (Number(c.grand_total) || 0);
    return Math.max(0, share - gt);
  };

  const setProjectOutcome = async (newOutcome) => {
    setOutcomeMenuOpen(false);
    if ((project?.outcome || "pending") === newOutcome) return;
    const childCount = project?.children?.length || 0;
    const confirmMsg =
      newOutcome === "won"
        ? `Mark project ${project?.project_number} as WON? This cascades to all ${childCount} child part(s) and flips the linked RFQ to Won.`
        : newOutcome === "lost"
        ? `Mark project ${project?.project_number} as LOST? This cascades to all ${childCount} child part(s) and flips the linked RFQ to Lost.`
        : newOutcome === "dropped"
        ? `Mark project ${project?.project_number} as DROPPED? This cascades to all ${childCount} child part(s) and flips the linked RFQ to Dropped.`
        : `Reopen project ${project?.project_number} (set all ${childCount} child(ren) back to Pending)? The linked RFQ will be reopened too.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/projects/${id}/set-outcome`, { outcome: newOutcome, cascade_to_children: true });
      toast.success(newOutcome === "pending" ? "Project reopened" : `Project marked ${newOutcome.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const setChildOutcome = async (npdId, newOutcome) => {
    try {
      await api.post(`/npd/${npdId}/set-outcome`, { outcome: newOutcome });
      toast.success(newOutcome === "pending" ? "Reopened" : `Marked ${newOutcome.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto" data-testid="project-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/npd"))} className="p-2 hover:bg-slate-100 rounded-md text-slate-500" data-testid="project-back-btn"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Briefcase size={20} className="text-orange-600" />
              {project.project_name}
              {project.outcome === "won" && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid="project-outcome-won"><Award size={12} /> Won</span>
              )}
              {project.outcome === "lost" && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded" data-testid="project-outcome-lost">Lost</span>
              )}
              {project.outcome === "dropped" && (
                <span className="text-xs bg-stone-200 text-stone-800 px-2 py-0.5 rounded" data-testid="project-outcome-dropped">Dropped</span>
              )}
              {project.outcome === "partially_awarded" && (
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded" data-testid="project-outcome-partial">Partially Awarded</span>
              )}
              {/* Project-level approval rollup — Approved / Under Approval / Draft.
                  Hidden once the project is Dropped/Lost (approval no longer relevant). */}
              {(project.child_count ?? 0) > 0 && !["dropped", "lost"].includes(project.outcome) && (() => {
                const st = project.approval_state || (project.all_children_approved ? "approved" : "draft");
                const cls = st === "approved" ? "bg-emerald-100 text-emerald-700"
                          : st === "under_approval" ? "bg-amber-100 text-amber-800"
                          : "bg-slate-100 text-slate-600";
                const label = st === "approved" ? "Approved" : st === "under_approval" ? "Under Approval" : "Draft";
                const title = st === "approved" ? "All parts approved for export"
                            : st === "under_approval" ? "Some parts awaiting manager approval"
                            : "Not yet sent for approval";
                return <span className={`text-xs px-2 py-0.5 rounded ${cls}`} title={title} data-testid={`project-${st}`}>{label}</span>;
              })()}
            </h1>
            <div className="text-xs text-slate-500 font-mono mt-0.5">{project.project_number}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(project.children?.length || 0) > 0 && (
            <>
              <button
                onClick={applyDefaults}
                className="px-3 py-2 text-sm border border-orange-400 text-orange-700 hover:bg-orange-50 rounded-md flex items-center gap-1.5"
                data-testid="project-apply-defaults"
                title="Push current project defaults (overheads, scrap factor, tool maint %) onto every child part"
              >
                <RefreshCw size={14} /> Apply Defaults
              </button>
              <button
                onClick={exportExcel}
                disabled={exportingXlsx}
                className={`px-3 py-2 text-sm rounded-md flex items-center gap-1.5 transition-all duration-150 active:scale-95 disabled:cursor-wait ${
                  exportingXlsx ? "bg-slate-400 text-white/90 ring-2 ring-slate-300" : "bg-slate-900 hover:bg-slate-800 active:bg-slate-700 text-white"
                }`}
                data-testid="project-export"
              >
                {exportingXlsx ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                {exportingXlsx ? "Exporting…" : "Export Excel"}
              </button>
              <button
                onClick={downloadAllQuotes}
                disabled={exportingZip}
                className={`px-3 py-2 text-sm border rounded-md flex items-center gap-1.5 transition-all duration-150 active:scale-95 disabled:cursor-wait ${
                  exportingZip ? "bg-slate-100 border-slate-200 text-slate-400 ring-2 ring-slate-200" : "border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                }`}
                data-testid="project-export-zip"
                title="Download every part's quote as individual Excel files in one ZIP"
              >
                {exportingZip ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                {exportingZip ? "Zipping…" : "All Quotes (ZIP)"}
              </button>
              <button
                onClick={() => window.open(`/projects/${id}/preview`, "_blank")}
                className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-1.5"
                data-testid="project-preview"
                title="Open project preview in a new tab"
              >
                <Eye size={14} /> Preview
              </button>
              {(() => {
                const o = project.outcome || "pending";
                const triggerClass =
                  o === "won" ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" :
                  o === "lost" ? "bg-red-600 text-white border-red-600 hover:bg-red-700" :
                  o === "dropped" ? "bg-stone-600 text-white border-stone-600 hover:bg-stone-700" :
                  o === "partially_awarded" ? "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200" :
                  "border-slate-300 text-slate-700 hover:bg-slate-50";
                const triggerLabel =
                  o === "won" ? "Won" :
                  o === "lost" ? "Lost" :
                  o === "dropped" ? "Dropped" :
                  o === "partially_awarded" ? "Partial" :
                  "Mark Outcome";
                return (
                  <div className="relative inline-block" data-testid="project-outcome-menu">
                    <button
                      type="button"
                      onClick={() => setOutcomeMenuOpen((v) => !v)}
                      onBlur={() => setTimeout(() => setOutcomeMenuOpen(false), 150)}
                      className={`px-3 py-2 text-sm rounded-md border flex items-center gap-1.5 ${triggerClass}`}
                      data-testid="project-outcome-trigger"
                    >
                      <Award size={14} /> {triggerLabel}
                      <ChevronDown size={12} className={`transition ${outcomeMenuOpen ? "rotate-180" : ""}`} />
                    </button>
                    {outcomeMenuOpen && (
                      <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1">
                        <button type="button" onClick={() => setProjectOutcome("won")} className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm flex items-center gap-2 text-emerald-700" data-testid="project-outcome-won-item">
                          <Award size={14} /> Mark Won
                        </button>
                        <button type="button" onClick={() => setProjectOutcome("lost")} className="w-full text-left px-3 py-2 hover:bg-red-50 text-sm text-red-700" data-testid="project-outcome-lost-item">
                          Mark Lost
                        </button>
                        <button type="button" onClick={() => setProjectOutcome("dropped")} className="w-full text-left px-3 py-2 hover:bg-stone-50 text-sm text-stone-700" data-testid="project-outcome-dropped-item">
                          Mark Dropped
                        </button>
                        {o !== "pending" && (
                          <button type="button" onClick={() => setProjectOutcome("pending")} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-600 border-t border-slate-100" data-testid="project-outcome-reopen-item">
                            ↺ Reopen
                          </button>
                        )}
                        <div className="border-t border-slate-100 mt-1 pt-1 px-3 pb-1 text-[10px] text-slate-400">
                          Cascades to all {project?.children?.length || 0} child part(s)
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          <button
            onClick={() => nav(`/projects/${id}/edit`)}
            className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-1.5"
            data-testid="project-edit"
            title="Edit this project's details (name, customer, dates, defaults)"
          >
            <Pencil size={14} /> Edit
          </button>
          <button
            onClick={openCloneModal}
            className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-1.5"
            data-testid="project-clone"
            title="Clone this project and all its parts into a new draft project"
          >
            <Copy size={14} /> Clone Project
          </button>
          {isAdmin && (
            <button
              onClick={deleteProject}
              className="px-3 py-2 text-sm border border-red-300 text-red-700 hover:bg-red-50 rounded-md flex items-center gap-1.5"
              data-testid="project-delete"
            >
              <Trash2 size={14} /> Soft-delete
            </button>
          )}
          {(project.children?.length || 0) > 0 && (
            <button
              onClick={() => nav(`/cost-breakdown?project=${id}`)}
              className="px-3 py-2 text-sm border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md flex items-center gap-1.5"
              data-testid="project-breakdown-jump"
              title="Open the Cost Breakdown page filtered to this project"
            >
              <FileText size={14} /> Cost Breakdown
            </button>
          )}
          {/* Proto projects — customer price negotiation (target vs settled) + approve-all.
              Gated by the customer_price.manage permission (set per role in Settings → Roles). */}
          {isProto && (project.children?.length || 0) > 0 && (isAdmin || hasPermission("customer_price.manage")) && (
            <>
              <button
                onClick={() => setPriceModal("target")}
                className="px-3 py-2 text-sm border border-sky-300 text-sky-700 hover:bg-sky-50 rounded-md flex items-center gap-1.5"
                data-testid="project-customer-target"
                title="Customer Target Price — the price the customer is targeting; see the costing breakdown summary"
              >
                <Award size={14} /> Customer Target Price
              </button>
              <button
                onClick={() => setPriceModal("settled")}
                className="px-3 py-2 text-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-md flex items-center gap-1.5"
                data-testid="project-customer-settled"
                title="Customer Settled Price — enter the agreed price after negotiation, then approve all parts in one click"
              >
                <Award size={14} /> Customer Settled Price
              </button>
            </>
          )}
        </div>
      </div>

      {/* Project info card */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Project Info</h2>
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <InfoRow label="Customer Name" value={project.customer_name || (customersForClone.find((c) => c.customer_id === project.customer_id) || {}).name || "—"} />
          <InfoRow label="End Customer" value={project.end_customer || "—"} />
          <InfoRow label="Due Date" value={project.customer_due_date || "—"} />
          <InfoRow label="Validity" value={project.quote_validity_date || "—"} />
          <InfoRow label="Scrap Factor" value={String(project.default_scrap_recovery_factor)} />
          <InfoRow label="Tool Maint Default" value={`${project.default_tool_maintenance_pct}%`} />
          <InfoRow label="Status" value={project.derived_status ? (project.derived_status === "Miscellaneous" ? "Miscellaneous" : project.derived_status.toUpperCase()) : (project.status || "draft").toUpperCase()} />
        </dl>
        {(() => {
          const parts = project.children || [];
          const partsValue = parts.reduce((s, c) => s + (Number(c.total_project_value) || 0), 0);
          const isProto = !!project.is_prototype;
          // Prototype project value = prototype pieces value + development cost (one-off run).
          const devTotal = Number(project.children_development_cost_total) || 0;
          // When a Customer Settled Price is agreed, the project value is EXACTLY the settled
          // total the user typed (no per-part rounding drift). Use the saved sheet total directly.
          const settledSheet = isProto ? project.customer_settled_sheet : null;
          let settledTotal = null;
          if (settledSheet?.summary) {
            const sm = settledSheet.summary;
            const gt = Number(sm.grand_total) || 0;
            const dv = Number(sm.dev) || 0;
            settledTotal = sm.quote_basis === "combined"
              ? (sm.grand_total_with_dev != null ? Number(sm.grand_total_with_dev) : gt + dv)
              : gt + dv;
          }
          const totalBV = settledTotal != null && !isNaN(settledTotal)
            ? settledTotal
            : (isProto ? partsValue + devTotal : partsValue);
          return (
            <div className="mt-4 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3" data-testid="project-total-business-value">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Total {isProto ? "Prototype " : "Business "}Value</div>
                <div className="text-[11px] text-emerald-700/70">
                  {isProto
                    ? `Prototype pieces (qty × grand total) + development cost, across all ${parts.length} part${parts.length === 1 ? "" : "s"}`
                    : `Sum of project value across all ${parts.length} part${parts.length === 1 ? "" : "s"} (per-piece × volumes over project life)`}
                </div>
              </div>
              <div className="text-xl font-bold font-mono text-emerald-800 whitespace-nowrap">{formatINR(totalBV)}</div>
            </div>
          );
        })()}
        {project.remarks && (
          <div className="mt-3 text-sm text-slate-600 border-t border-slate-100 pt-3">{project.remarks}</div>
        )}
      </div>

      {/* R35 — Project sourcing rollup */}
      <ProjectSourcingRollup rollup={project.sourcing_rollup} />

      {/* Machine requirement rollup — whole machines needed across all parts */}
      <ProjectMachineRollup mr={project.machine_requirement} />

      {/* Parts table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            Parts ({project.children?.length || 0})
          </h2>
        </div>
        {project.children?.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            No parts yet. Add one below.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="project-parts-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">NPD #</th>
                <th className="text-left p-3">Part</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Outcome</th>
                <th className="text-right p-3">Grand Total</th>
                <th className="text-right p-3">Development Cost</th>
                {!hideMargin && <th className="text-right p-3">Margin %</th>}
                <th className="text-right p-3 w-24">Quote</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(project.children || []).map((c) => (
                <tr key={c.npd_id} className="hover:bg-slate-50">
                  <td className="p-3 text-slate-400 font-mono text-xs">P{c.child_index}</td>
                  <td className="p-3 font-mono text-xs text-slate-600">{c.npd_number}</td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{c.part_name}</div>
                    <div className="text-xs text-slate-500">{c.part_number}</div>
                  </td>
                  <td className="p-3">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      {(c.status || "draft").toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3">
                    <select
                      value={c.outcome || "pending"}
                      onChange={(e) => setChildOutcome(c.npd_id, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 bg-white font-medium ${
                        c.outcome === "won" ? "border-emerald-400 text-emerald-700" :
                        c.outcome === "lost" ? "border-red-400 text-red-700" :
                        c.outcome === "dropped" ? "border-stone-400 text-stone-700" :
                        "border-slate-300 text-slate-600"
                      }`}
                      data-testid={`child-outcome-${c.npd_number}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="won">Won</option>
                      <option value="lost">Lost</option>
                      <option value="dropped">Dropped</option>
                    </select>
                  </td>
                  <td className="p-3 text-right font-mono">
                    {c.customer_settled_price != null && c.customer_settled_price !== "" ? (
                      <span className="text-emerald-700 font-semibold" title="Customer Settled Price (negotiated ₹/pc)">{formatINR(Number(c.customer_settled_price))}</span>
                    ) : formatINR(c.grand_total)}
                  </td>
                  <td className="p-3 text-right font-mono text-slate-600" data-testid={`part-devcost-${c.npd_number}`} title={c.customer_settled_price != null && c.customer_settled_price !== "" ? `Settled dev (raw tooling ${formatINR(c.total_development_cost)})` : "Development / tooling"}>{formatINR(settledPartDev(c))}</td>
                  {!hideMargin && (
                    <td className="p-3 text-right" data-testid={`part-margin-${c.npd_number}`}>
                      {c.margin_pct === null || c.margin_pct === undefined ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span className={`font-mono ${c.margin_red_flag ? "text-red-600 font-semibold" : "text-slate-700"}`}>
                          {c.margin_pct?.toFixed(2)}%
                          {c.margin_red_flag && <AlertTriangle size={12} className="inline ml-1" />}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="p-3 text-right">
                    <button
                      onClick={() => clonePart(c)}
                      className="inline-flex items-center text-slate-500 hover:text-orange-600 px-2 py-1 mr-1"
                      title="Clone this part as a new part in this project"
                      data-testid={`clone-part-${c.npd_id}`}
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => copyToClipboard("npd", c.npd_id, c.part_name || c.npd_number)}
                      className="inline-flex items-center text-slate-500 hover:text-indigo-600 px-2 py-1 mr-1"
                      title="Copy this part — paste into any other project"
                      data-testid={`copy-part-${c.npd_id}`}
                    >
                      <ClipboardCopy size={14} />
                    </button>
                    <button
                      onClick={() => convertToSingle(c)}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 px-2 py-1 mr-1"
                      title="Make this a single costing — moves the part out of this project with a new NPD number"
                      data-testid={`part-to-single-${c.npd_id}`}
                    >
                      <NotepadText size={15} />
                    </button>
                    {c.approved_for_export && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded mr-2" data-testid={`part-approved-${c.npd_number}`}>Approved</span>
                    )}
                    <span className="inline-flex items-center gap-2 whitespace-nowrap align-middle">
                      <button type="button" onClick={() => setPdfNpd(c)} disabled={!!pdfNpd} className="text-slate-500 hover:text-orange-600 p-1 disabled:opacity-40" title="Download quote as PDF" data-testid={`part-download-${c.npd_number}`}>
                        <Download size={16} />
                      </button>
                      <a href={`/npd/${c.npd_id}/preview`} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="View Quote" data-testid={`part-view-${c.npd_number}`}>
                        <Eye size={16} />
                      </a>
                      <Link to={`/npd/${c.npd_id}?from=project`} className="text-orange-600 hover:text-orange-700 p-1" title="Edit Quote" data-testid={`part-edit-${c.npd_number}`}>
                        <Pencil size={16} />
                      </Link>
                      {canDeletePart && (
                        <button
                          onClick={() => deletePart(c)}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Delete this part (moves to Trash)"
                          data-testid={`part-delete-${c.npd_number}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5" data-testid="to-single-note">
            <NotepadText size={13} className="text-blue-600 shrink-0" />
            <span>
              <span className="text-blue-700 font-medium">Make single costing</span> — moves that part out of this
              project and gives it its own NPD number. The costing, rounds and attachments stay with it; the remaining
              parts renumber to P1, P2, …
            </span>
          </p>
          </div>
        )}
      </div>

      {/* Bulk drawing upload */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4" data-testid="bulk-upload-block">
        <h3 className="text-sm font-semibold text-sky-900 mb-2 flex items-center gap-2">
          <Upload size={14} /> Bulk Drawing Upload
        </h3>
        <p className="text-xs text-sky-700/80 mb-3">
          Drop multiple PDFs / images here. Each file becomes a child part stub with the drawing already attached.
          Part name defaults to the filename — rename it on each part page later. Part number is a placeholder (you must edit it).
        </p>
        <label className="flex items-center justify-center border-2 border-dashed border-sky-300 rounded-md p-6 cursor-pointer hover:bg-sky-100 transition">
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.bmp"
            disabled={bulkBusy}
            onChange={(e) => bulkUpload(e.target.files)}
            className="hidden"
            data-testid="bulk-upload-input"
          />
          <div className="text-center text-sky-800">
            <Upload size={28} className="mx-auto mb-1 opacity-70" />
            <div className="text-sm font-medium">
              {bulkBusy ? "Uploading…" : "Click to select files (or drag drop) — up to 50 at once"}
            </div>
            <div className="text-[11px] text-sky-700/70 mt-1">PDF, JPG, PNG, TIF · max 10 MB each</div>
          </div>
        </label>
        {bulkResult && bulkResult.error_count > 0 && (
          <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded p-2">
            <div className="font-semibold text-amber-800 mb-1">
              {bulkResult.error_count} file(s) failed:
            </div>
            <ul className="list-disc ml-5 text-amber-700">
              {bulkResult.errors.map((e, i) => (
                <li key={i}>{e.filename}: {e.error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add Part inline form */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="add-part-block">
        <h3 className="text-sm font-semibold text-orange-900 mb-2 flex items-center gap-2">
          <Plus size={14} /> Add Part to Project
        </h3>
        <p className="text-xs text-orange-700/80 mb-3">
          Part inherits the project's customer, due date, validity, overheads, scrap factor, and tool maintenance %.
          You'll edit the rest (material, processes, BOP, treatments, dimensions, drawing) on the part page.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            value={newPart.part_name}
            onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
            placeholder="Part name"
            className="input"
            data-testid="add-part-name"
          />
          <input
            value={newPart.part_number}
            onChange={(e) => setNewPart({ ...newPart, part_number: e.target.value })}
            placeholder="Part number"
            className="input"
            data-testid="add-part-number"
          />
          <button
            onClick={addPart}
            disabled={adding}
            className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2"
            data-testid="add-part-btn"
          >
            <FileText size={14} /> {adding ? "Adding…" : "Add Part"}
          </button>
        </div>

        {/* Paste a part/assembly copied from anywhere — no need to rebuild the same quote. */}
        <div className="mt-3 pt-3 border-t border-orange-200 flex flex-wrap items-center gap-2">
          <button
            onClick={pastePart}
            disabled={pasting || !clip}
            className="bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 text-sm font-semibold rounded-md px-4 py-2 flex items-center gap-2"
            data-testid="paste-part-btn"
          >
            <ClipboardPaste size={14} /> {pasting ? "Pasting…" : "Paste part"}
          </button>
          {clip ? (
            <span className="text-xs text-slate-600">
              Clipboard: <span className="font-medium">{clip.label || clip.id}</span>
              <span className="text-slate-400"> ({clip.kind === "assembly" ? "assembly" : "part"})</span>
              {" "}— pastes as a new, fully-editable part.
            </span>
          ) : (
            <span className="text-xs text-slate-400">Copy a part (clipboard icon) on any project or costing, then paste it here.</span>
          )}
        </div>
      </div>

      {project && (
        <div className="mt-6">
          <MiscFilesPanel
            entityType="projects"
            entityId={id}
            files={project.misc_files || []}
            canEdit={user?.role === "admin" || project.assigned_to === user?.user_id || (!!user?.unit_id && project.unit_id === user?.unit_id)}
            onChange={load}
          />
        </div>
      )}

      {/* Round 21.5 — Child assemblies section */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6" data-testid="project-assemblies-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <Layers size={16} className="text-sky-600" />
            Assemblies ({(project.child_assemblies || []).length})
          </h2>
          <button
            onClick={addAssembly}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-semibold"
            data-testid="add-assembly-btn"
          >
            <Plus size={14} /> Add Assembly
          </button>
        </div>
        {(project.child_assemblies || []).length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded">
            No assemblies yet. Click "+ Add Assembly" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm" data-testid="project-assemblies-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-3 font-medium">ASM #</th>
                <th className="text-left py-2 px-3 font-medium">Name</th>
                <th className="text-left py-2 px-3 font-medium">Sub-parts</th>
                <th className="text-left py-2 px-3 font-medium">Status</th>
                <th className="text-right py-2 px-3 font-medium">Grand Total</th>
                <th className="text-right py-2 px-3 font-medium">Development Cost</th>
                {!hideMargin && <th className="text-right py-2 px-3 font-medium">Margin %</th>}
                <th className="text-right py-2 px-3 font-medium">Quote</th>
              </tr>
            </thead>
            <tbody>
              {(project.child_assemblies || []).map((a) => (
                <tr key={a.assembly_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`asm-row-${a.assembly_number}`}>
                  <td className="py-2 px-3 font-mono text-xs">{a.assembly_number}</td>
                  <td className="py-2 px-3">{a.assembly_name}</td>
                  <td className="py-2 px-3 text-slate-600">{(a.components || []).length}</td>
                  <td className="py-2 px-3">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100">{(a.status || "draft").toUpperCase()}</span>
                    {a.is_awarded && <span className="text-xs px-2 py-0.5 ml-1 rounded bg-amber-100 text-amber-800">Awarded</span>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{formatINR(a.grand_total)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-600">{formatINR(a.development_cost_total)}</td>
                  {!hideMargin && (
                    <td className="py-2 px-3 text-right font-mono text-slate-700">{a.margin_pct != null ? `${Number(a.margin_pct).toFixed(2)}%` : "—"}</td>
                  )}
                  <td className="py-2 px-3 text-right">
                    <span className="inline-flex items-center gap-2 whitespace-nowrap">
                      <button type="button" onClick={() => setPdfAsmId(a.assembly_id)} disabled={!!pdfAsmId} className="text-slate-500 hover:text-orange-600 p-1 disabled:opacity-40" title="Download quote as PDF" data-testid={`asm-download-${a.assembly_number}`}>
                        <Download size={16} />
                      </button>
                      <a href={`/assemblies/${a.assembly_id}/preview`} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="View Quote" data-testid={`asm-view-${a.assembly_number}`}>
                        <Eye size={16} />
                      </a>
                      <Link to={`/assemblies/${a.assembly_id}`} className="text-orange-600 hover:text-orange-700 p-1" title="Edit Assembly" data-testid={`asm-edit-${a.assembly_number}`}>
                        <Pencil size={16} />
                      </Link>
                      <button type="button" onClick={() => cloneAssembly(a)} className="text-slate-400 hover:text-orange-600 p-1" title="Clone this assembly" data-testid={`asm-clone-${a.assembly_number}`}>
                        <Copy size={16} />
                      </button>
                      <button type="button" onClick={() => copyToClipboard("assembly", a.assembly_id, a.assembly_name || a.assembly_number)} className="text-slate-400 hover:text-indigo-600 p-1" title="Copy this assembly — paste into any other project" data-testid={`asm-copy-${a.assembly_number}`}>
                        <ClipboardCopy size={16} />
                      </button>
                      {user?.role === "admin" && (
                        <button type="button" onClick={() => deleteAssembly(a)} className="text-slate-400 hover:text-red-600 p-1" title="Delete (admin only)" data-testid={`asm-delete-${a.assembly_number}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Per-part cost breakdown — all formulas/sections, collapsed to totals with a drill-down.
          Placed below Assemblies; reachable via the "Cost Breakdown" header button. */}
      <ProjectPartBreakdown parts={project.children} hideMargin={hideMargin} onDownloadExcel={exportExcel} projectId={id} customerId={project.customer_id} onReload={load} />

      <ProjectRoundsSection
        rounds={rounds}
        project={project}
        comparingRound={comparingRound}
        setComparingRound={setComparingRound}
      />

      {/* Round 33 — Clone modal placed inside ProjectDetail (not ProjectRoundsSection) so the state is in scope */}
      {cloneModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 flex items-center justify-center p-4" data-testid="clone-modal-backdrop">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="clone-modal">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Copy size={18} className="text-orange-600" /> Clone project
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Source: <span className="font-mono">{project?.project_number}</span> · {project?.children?.length || 0} parts
            </p>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Project name for the clone</label>
              <input
                type="text"
                value={cloneProjectName}
                onChange={(e) => setCloneProjectName(e.target.value)}
                placeholder="Name for the new project…"
                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                data-testid="clone-project-name"
                disabled={cloning}
              />
              {!(cloneProjectName || "").trim() && (
                <div className="text-[11px] text-red-600 mt-1">⚠ Project name is required.</div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Customer name for the clone</label>
              <div ref={customerComboRef} className="relative">
                <div className="flex items-stretch">
                  <input
                    type="text"
                    value={cloneCustomerName}
                    onChange={(e) => setCloneCustomerName(e.target.value)}
                    placeholder="Pick from list or type a new customer name…"
                    className="w-full px-2 py-2 border border-slate-300 rounded-l rounded-r-none text-sm bg-white border-r-0"
                    data-testid="clone-customer-name"
                    disabled={cloning}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setCustomerListOpen((v) => !v)}
                    className="px-2 border border-slate-300 rounded-r bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                    disabled={cloning}
                    aria-label="Toggle customer list"
                    data-testid="clone-customer-dropdown-toggle"
                  >
                    <ChevronDown size={16} className={`text-slate-600 transition ${customerListOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {customerListOpen && (
                  <ul
                    className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm"
                    data-testid="clone-customer-list"
                  >
                    {customersForClone.length === 0 && (
                      <li className="px-3 py-2 text-slate-400 italic">No customers in master.</li>
                    )}
                    {customersForClone.map((c) => (
                      <li
                        key={c.customer_id}
                        onClick={() => {
                          setCloneCustomerName(c.name);
                          setCustomerListOpen(false);
                        }}
                        className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-800"
                      >
                        {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {(() => {
                const typed = (cloneCustomerName || "").trim();
                const sourceName =
                  (customersForClone.find((c) => c.customer_id === project?.customer_id) || {}).name || "";
                if (!typed) {
                  return <div className="text-[11px] text-red-600 mt-1">⚠ Customer name is required.</div>;
                }
                if (typed.toLowerCase() === sourceName.toLowerCase()) {
                  return <div className="text-[11px] text-slate-500 mt-1">Same as source customer — no change.</div>;
                }
                const existsInMaster = customersForClone.some(
                  (c) => (c.name || "").trim().toLowerCase() === typed.toLowerCase()
                );
                if (existsInMaster) {
                  return (
                    <div className="text-[11px] text-amber-700 mt-1">
                      ⚠ Customer will change from "{sourceName}" to "{typed}" (already in master) on the cloned project + all parts.
                    </div>
                  );
                }
                return (
                  <div className="text-[11px] text-amber-700 mt-1">
                    ⚠ "{typed}" is NOT in the customer master — a new customer record will be auto-created and the clone + all parts will use it.
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setCloneModalOpen(false)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700 hover:bg-slate-50"
                disabled={cloning}
                data-testid="clone-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmCloneProject}
                className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-semibold disabled:bg-orange-300"
                disabled={cloning || !(cloneCustomerName || "").trim() || !(cloneProjectName || "").trim()}
                data-testid="clone-confirm"
              >
                {cloning ? "Cloning…" : "Clone"}
              </button>
            </div>
          </div>
        </div>
      )}

      <QuotePdfDownloader npd={pdfNpd} onDone={() => setPdfNpd(null)} />
      {pdfAsmId && <AssemblyPdfDownloader assemblyId={pdfAsmId} onDone={() => setPdfAsmId(null)} />}
      {priceModal && (
        <CustomerPriceModal
          mode={priceModal}
          project={project}
          projectId={id}
          hideMargin={hideMargin}
          canApprove={isAdmin || hasPermission("customer_price.manage")}
          onClose={() => setPriceModal(null)}
          onReload={load}
        />
      )}
    </div>
  );
}

// Customer price negotiation modal (proto projects) — a FULLY EDITABLE worksheet.
// Edit the breakdown summary (A–E + Dev), each part's Qty / AAMPL / Settled ₹/pc; all
// totals recompute live. The whole negotiated sheet is saved so nothing reverts.
// Settled mode can also approve every part in one click once the price is agreed.
function CustomerPriceModal({ mode, project, projectId, hideMargin, canApprove, onClose, onReload }) {
  const nav = useNavigate();
  const isSettled = mode === "settled";
  const field = isSettled ? "customer_settled_price" : "customer_target_price";
  const parts = (project.children || []);
  const saved = project[`customer_${mode}_sheet`] || null;
  const savedRow = (npdId) => (saved?.parts || []).find((r) => r.npd_id === npdId);
  const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? 0 : Number(v));

  const qtyDefault = (c) => Math.max(1, Number(c.prototype_qty) || 1);
  const aamplDefault = (c) => Number(c.grand_total) || 0;
  const priceDefault = (c) => {
    const existing = c[field];
    if (existing !== null && existing !== undefined && existing !== "") return Number(existing);
    if (isSettled && c.customer_target_price != null && c.customer_target_price !== "") return Number(c.customer_target_price);
    return aamplDefault(c);
  };

  // Per-part editable rows (hydrate from the saved sheet if present).
  const [rows, setRows] = useState(() => parts.map((c) => {
    const sr = savedRow(c.npd_id);
    return {
      npd_id: c.npd_id,
      part_name: c.part_name || "(unnamed)",
      part_number: c.part_number || c.npd_number,
      qty: sr?.qty ?? qtyDefault(c),
      aampl: sr?.aampl ?? aamplDefault(c),
      price: sr?.price ?? priceDefault(c),
    };
  }));
  const setRow = (i, key, val) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  // Editable breakdown summary (money). Default = engine aggregate across parts.
  const engineAgg = parts.reduce((a, c) => {
    const q = qtyDefault(c);
    a.net_rmc += (Number(c.net_rmc) || 0) * q;
    a.process += (Number(c.process_cost_total) || 0) * q;
    a.bop += (Number(c.bop_cost_total) || 0) * q;
    a.treat += (Number(c.treatment_total) || 0) * q;
    a.ohm += (Number(c.overheads_and_margin) || 0) * q;
    a.dev += Number(c.total_development_cost) || 0;
    return a;
  }, { net_rmc: 0, process: 0, bop: 0, treat: 0, ohm: 0, dev: 0 });
  const [summary, setSummary] = useState(() => {
    const s = saved?.summary;
    return {
      net_rmc: s?.net_rmc ?? engineAgg.net_rmc,
      process: s?.process ?? engineAgg.process,
      bop: s?.bop ?? engineAgg.bop,
      treat: s?.treat ?? engineAgg.treat,
      ohm: s?.ohm ?? engineAgg.ohm,
      dev: s?.dev ?? engineAgg.dev,
    };
  });
  const setSum = (key, val) => setSummary((s) => ({ ...s, [key]: val }));

  // How the customer wants the price quoted: parts total and tooling as two separate
  // figures, or one combined number (Grand Total + Development/Tooling).
  const [quoteBasis, setQuoteBasis] = useState(() => saved?.summary?.quote_basis || "separate");
  const combined = quoteBasis === "combined";

  // Combined basis absorbs tooling into the quoted grand total → the separate
  // Development / Tooling line is always 0 (covers both selecting it and loading a doc
  // that was saved on the combined basis), so it can never double-count.
  useEffect(() => {
    if (combined && num(summary.dev) !== 0) setSum("dev", 0);
  }, [combined]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saving, setSaving] = useState(false);

  const grandSummary = num(summary.net_rmc) + num(summary.process) + num(summary.bop) + num(summary.treat) + num(summary.ohm);
  const grandPlusDev = grandSummary + num(summary.dev);
  const partsAampl = rows.reduce((s, r) => s + num(r.aampl) * num(r.qty), 0);
  const partsMode = rows.reduce((s, r) => s + num(r.price) * num(r.qty), 0);
  const totalAampl = partsAampl + (combined ? num(summary.dev) : 0);
  const totalMode = partsMode + (combined ? num(summary.dev) : 0);
  const pctOf = (v) => (grandSummary > 0 ? `${((num(v) / grandSummary) * 100).toFixed(1)}%` : null);

  // Grand Total is editable: type a target total (e.g. 20 lakhs) → it is split EQUALLY
  // across the parts (each part = total ÷ N, settled ₹/pc = that ÷ qty) and the gap is
  // absorbed by Process Cost ONLY — RM (Net RMC), scrap and Margin are never touched.
  const [gtEditing, setGtEditing] = useState(false);
  const [gtText, setGtText] = useState("");
  const applyGrandTotal = (gRaw) => {
    const G = num(gRaw);
    const N = rows.length;
    if (N > 0) {
      const per = G / N;                                   // equal money per part
      setRows((rs) => rs.map((r) => ({ ...r, price: num(r.qty) > 0 ? per / num(r.qty) : 0 })));
    }
    setSummary((s) => {
      const A = num(s.net_rmc), C = num(s.bop), D = num(s.treat), E = num(s.ohm);
      const B = Math.max(0, G - A - C - D - E);            // only Process flexes; RM/scrap/margin fixed
      return { ...s, process: B };
    });
  };

  const save = async (approve) => {
    if (approve && !window.confirm(`Approve the settled prices and LOCK all ${parts.length} part(s) as Approved?\n\nThis marks every part Approved for export.`)) return;
    setSaving(true);
    try {
      const sheet = {
        summary: {
          ...Object.fromEntries(Object.entries(summary).map(([k, v]) => [k, num(v)])),
          grand_total: grandSummary,
          grand_total_with_dev: grandPlusDev,
          quote_basis: quoteBasis,            // "separate" | "combined"
        },
        parts: rows.map((r) => ({ npd_id: r.npd_id, qty: num(r.qty), aampl: num(r.aampl), price: num(r.price) })),
      };
      const body = {
        mode,
        approve: !!approve,
        sheet,
        prices: rows.map((r) => ({ npd_id: r.npd_id, price: num(r.price), qty: Math.max(1, Math.round(num(r.qty)) || 1) })),
      };
      const r = await api.post(`/projects/${projectId}/customer-prices`, body);
      toast.success(approve
        ? `Settled prices saved · ${r.data.approved_count || parts.length} part(s) approved`
        : `${isSettled ? "Settled" : "Target"} worksheet saved`);
      await onReload();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Indian-comma display for editable boxes: show grouped digits (20,00,000) when NOT being
  // typed in, and raw digits while focused (so typing/cursor stays sane). One shared focus key.
  const [focusedField, setFocusedField] = useState(null);
  const fmtIN = (v) => (v === "" || v == null || isNaN(Number(v)) ? v : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));

  // Plain-function row builders (NOT React components, so inputs keep focus while typing).
  const sumInput = (k, locked = false) => (
    <input
      type="text" inputMode="decimal"
      value={locked ? fmtIN(0) : (focusedField === `sum-${k}` ? (summary[k] ?? "") : fmtIN(summary[k]))}
      onFocus={() => !locked && setFocusedField(`sum-${k}`)}
      onBlur={() => setFocusedField(null)}
      onChange={(e) => !locked && setSum(k, e.target.value.replace(/[^0-9.]/g, ""))}
      disabled={locked}
      title={locked ? "Tooling is absorbed into the quoted total on the combined basis — it bills as 0 separately." : undefined}
      className={`w-32 px-2 py-1 border rounded text-sm font-mono text-right ${locked ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed" : "border-slate-300 bg-white"}`}
      data-testid={`sum-${k}`}
    />
  );
  const summaryRow = (label, k, pct, locked) => (
    <div className="flex items-center justify-between px-3 py-1.5 text-slate-600">
      <span className="flex items-center gap-2">
        {label}
        {pct != null && <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1 py-0.5">{pct}</span>}
        {locked && <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1 py-0.5" title="Held fixed when you split the Grand Total — only Process Cost changes">🔒 fixed on total-split</span>}
      </span>
      {sumInput(k)}
    </div>
  );
  // Tickable quote-basis option (parts + tooling separate, or one combined figure).
  const basisOption = (key, title, sub, value) => {
    const on = quoteBasis === key;
    return (
      <button
        type="button"
        onClick={() => {
          setQuoteBasis(key);
          // "Grand Total + Tooling (one figure)" absorbs tooling into the quoted total,
          // so the separate Development / Tooling line must be 0 (else it double-counts).
          if (key === "combined") setSum("dev", 0);
        }}
        aria-pressed={on}
        className={`flex items-start gap-2 text-left border rounded-md px-3 py-2 transition ${
          on ? (isSettled ? "border-emerald-500 bg-emerald-50/60" : "border-sky-500 bg-sky-50/60") : "border-slate-200 hover:bg-slate-50"
        }`}
        data-testid={`quote-basis-${key}`}
      >
        <span className={`mt-0.5 w-4 h-4 shrink-0 rounded flex items-center justify-center text-[11px] font-bold ${
          on ? (isSettled ? "bg-emerald-600 text-white" : "bg-sky-600 text-white") : "bg-slate-100 text-transparent border border-slate-300"
        }`}>✓</span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-800">{title}</span>
          <span className="block text-[11px] text-slate-500">{sub}</span>
          <span className="block text-sm font-mono font-semibold text-slate-900 mt-1">{value}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="customer-price-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-5 py-3 flex items-center justify-between border-b ${isSettled ? "bg-emerald-50 border-emerald-200" : "bg-sky-50 border-sky-200"}`}>
          <div>
            <h3 className={`text-base font-bold ${isSettled ? "text-emerald-800" : "text-sky-800"}`}>
              {isSettled ? "Customer Settled Price" : "Customer Target Price"}
            </h3>
            <p className="text-[11px] text-slate-500">{project.project_number} · {parts.length} part{parts.length === 1 ? "" : "s"} · prototype · <span className="italic">all fields editable</span></p>
          </div>
          <button type="button" onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-full text-red-500 hover:text-white hover:bg-red-500 text-2xl leading-none font-bold transition" title="Close" data-testid="customer-price-close">×</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Editable costing breakdown summary (all parts, ₹) */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Costing breakdown — all parts (₹) · editable</div>
            {summaryRow("A. Net RMC", "net_rmc", pctOf(summary.net_rmc), true)}
            {summaryRow("B. Process Cost", "process", pctOf(summary.process))}
            {summaryRow("C. BOP Cost", "bop", pctOf(summary.bop))}
            {summaryRow("D. Treatment & Maint", "treat", pctOf(summary.treat))}
            {!hideMargin && summaryRow("E. Overheads & Margin", "ohm", pctOf(summary.ohm), true)}
            <div className="flex items-center justify-between px-3 py-2 font-bold text-slate-900 border-t border-slate-300">
              <span className="flex items-center gap-2">
                Grand Total
                <span className="text-[10px] font-normal text-slate-400">type a total → splits equally across parts · only Process Cost flexes (RM, scrap & margin stay same)</span>
              </span>
              <input
                type="text" inputMode="decimal"
                value={gtEditing ? gtText : fmtIN(grandSummary ? Number(grandSummary.toFixed(2)) : "")}
                onFocus={() => { setGtEditing(true); setGtText(grandSummary ? String(Number(grandSummary.toFixed(2))) : ""); }}
                onChange={(e) => setGtText(e.target.value.replace(/[^0-9.]/g, ""))}
                onBlur={() => { setGtEditing(false); applyGrandTotal(gtText); }}
                className="w-40 px-2 py-1 border border-slate-400 rounded text-sm font-mono text-right font-bold bg-amber-50"
                data-testid="sum-grand-total"
                title="Type the agreed total — it divides equally across all parts; Process Cost absorbs the change (RM, scrap & margin stay the same)"
              />
            </div>
            <div className="flex items-center justify-between px-3 py-1.5 text-slate-600">
              <span className="flex items-center gap-2">
                Development / Tooling (one-time)
                {!combined && engineAgg.dev > 0 && Math.abs(num(summary.dev) - engineAgg.dev) > 0.5 && (
                  <button
                    type="button"
                    onClick={() => setSum("dev", engineAgg.dev)}
                    className="text-[10px] font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded px-1.5 py-0.5"
                    title="The actual tooling across all parts. Click to charge it; leave 0 to absorb it into the quoted total."
                    data-testid="dev-use-real-tooling"
                  >
                    parts tooling = {formatINR(engineAgg.dev)} · use
                  </button>
                )}
              </span>
              {sumInput("dev", combined)}
            </div>
            {/* Grand Total + Development cost — the combined one-figure quote */}
            <div className={`flex items-center justify-between px-3 py-2 border-t font-bold ${isSettled ? "bg-emerald-50/60 border-emerald-200 text-emerald-900" : "bg-sky-50/60 border-sky-200 text-sky-900"}`}>
              <span className="flex items-center gap-2">
                Grand Total + Development / Tooling
                <span className="text-[10px] font-normal text-slate-500">{formatINR(grandSummary)} + {formatINR(num(summary.dev))}</span>
              </span>
              <span className="font-mono text-base" data-testid="sum-grand-plus-dev">{formatINR(grandPlusDev)}</span>
            </div>
          </div>

          {/* Which figure the customer needs — tick one */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Quote basis — tick the one the customer needs
            </div>
            <div className="grid sm:grid-cols-2 gap-2 p-3">
              {basisOption(
                "separate",
                "Grand Total and Tooling separate",
                "Part price quoted on its own · tooling billed as a separate one-time line",
                `${formatINR(grandSummary)}  +  ${formatINR(num(summary.dev))} tooling`,
              )}
              {basisOption(
                "combined",
                "Grand Total + Tooling (one figure)",
                "Tooling absorbed into the quoted total — a single number to the customer",
                formatINR(grandPlusDev),
              )}
            </div>
          </div>

          {/* Per-part editable price table */}
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-2">Part</th>
                  <th className="text-right p-2 w-20">Qty</th>
                  <th className="text-right p-2 w-32">AAMPL ₹/pc</th>
                  <th className="text-right p-2 w-32">{isSettled ? "Settled" : "Target"} ₹/pc</th>
                  <th className="text-right p-2 w-24">Δ%</th>
                  <th className="text-right p-2 w-36">Line Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => {
                  const aampl = num(r.aampl);
                  const p = num(r.price);
                  const delta = aampl > 0 ? ((p - aampl) / aampl) * 100 : 0;
                  return (
                    <tr key={r.npd_id}>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => nav(`/npd/${r.npd_id}`)}
                          className="text-left font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                          title="Open this part's costing page"
                          data-testid={`price-part-link-${i}`}
                        >
                          {r.part_name}
                        </button>
                        <div className="text-[11px] text-slate-500 font-mono">{r.part_number}</div>
                      </td>
                      <td className="p-2 text-right">
                        <input type="text" inputMode="numeric" value={focusedField === `qty-${i}` ? (r.qty ?? "") : fmtIN(r.qty)}
                          onFocus={() => setFocusedField(`qty-${i}`)} onBlur={() => setFocusedField(null)}
                          onChange={(e) => setRow(i, "qty", e.target.value.replace(/[^0-9.]/g, ""))}
                          className="w-16 px-2 py-1 border border-slate-300 rounded text-sm font-mono text-right bg-white" data-testid={`qty-input-${i}`} />
                      </td>
                      <td className="p-2 text-right">
                        <input type="text" inputMode="decimal" value={focusedField === `aampl-${i}` ? (r.aampl ?? "") : fmtIN(r.aampl)}
                          onFocus={() => setFocusedField(`aampl-${i}`)} onBlur={() => setFocusedField(null)}
                          onChange={(e) => setRow(i, "aampl", e.target.value.replace(/[^0-9.]/g, ""))}
                          className="w-24 px-2 py-1 border border-slate-300 rounded text-sm font-mono text-right bg-white" data-testid={`aampl-input-${i}`} />
                      </td>
                      <td className="p-2 text-right">
                        <input type="text" inputMode="decimal" value={focusedField === `price-${i}` ? (r.price ?? "") : fmtIN(r.price)}
                          onFocus={() => setFocusedField(`price-${i}`)} onBlur={() => setFocusedField(null)}
                          onChange={(e) => setRow(i, "price", e.target.value.replace(/[^0-9.]/g, ""))}
                          className={`w-24 px-2 py-1 border rounded text-sm font-mono text-right ${isSettled ? "border-emerald-300 bg-emerald-50/40" : "border-sky-300 bg-sky-50/40"}`} data-testid={`price-input-${i}`} />
                      </td>
                      <td className={`p-2 text-right font-mono text-xs ${delta < 0 ? "text-red-600" : delta > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                        {delta ? `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%` : "—"}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-700">{formatINR(p * num(r.qty))}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 font-semibold">
                {!combined && num(summary.dev) > 0 && (
                  <tr className="text-slate-600 font-normal">
                    <td className="p-2" colSpan={5}>Development / Tooling — quoted separately (one-time)</td>
                    <td className="p-2 text-right font-mono">{formatINR(num(summary.dev))}</td>
                  </tr>
                )}
                <tr>
                  <td className="p-2" colSpan={2}>
                    Total {isSettled ? "settled" : "target"} value {combined ? "(incl. tooling)" : "(parts only)"}
                  </td>
                  <td className="p-2 text-right font-mono text-slate-500">{formatINR(totalAampl)}</td>
                  <td className="p-2" colSpan={2}></td>
                  <td className="p-2 text-right font-mono text-slate-900">{formatINR(totalMode)}</td>
                </tr>
                {!combined && num(summary.dev) > 0 && (
                  <tr className="border-t border-slate-300">
                    <td className="p-2" colSpan={5}>Grand Total + Tooling</td>
                    <td className="p-2 text-right font-mono text-slate-900">{formatINR(partsMode + num(summary.dev))}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50" data-testid="customer-price-cancel">Cancel</button>
            <button onClick={() => save(false)} disabled={saving} className="px-4 py-2 text-sm border border-slate-300 text-slate-800 rounded-md hover:bg-slate-100 disabled:opacity-50" data-testid="customer-price-save">
              {saving ? "Saving…" : `Save ${isSettled ? "settled" : "target"} worksheet`}
            </button>
            {isSettled && (
              <button
                onClick={() => save(true)}
                disabled={saving || !canApprove}
                title={canApprove ? "Save settled prices and approve ALL parts" : "You don't have approve permission"}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="customer-price-approve-all"
              >
                <Award size={14} /> {saving ? "Working…" : "Approve & lock all parts"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-slate-900 font-medium mt-0.5">{value}</dd>
    </div>
  );
}

function ProjectRoundsSection({ rounds, project, comparingRound, setComparingRound }) {
  if (!rounds || rounds.length === 0) return null;
  const CURRENT_KEY = "_current_";
  const leftId = comparingRound?.left || rounds[rounds.length - 1].round_id;
  const rightId = comparingRound?.right || CURRENT_KEY;
  const resolveSide = (id) => {
    if (id === CURRENT_KEY) return { label: "Current", children: project?.children || [], grand_total_at_save: (project?.children || []).reduce((s, c) => s + (Number(c.grand_total) || 0), 0) };
    const r = rounds.find((x) => x.round_id === id);
    if (!r) return { label: "?", children: [], grand_total_at_save: 0 };
    const savedChildren = (r.snapshot?.children || []).map((c) => c.snapshot).filter(Boolean);
    return { label: r.round_label, children: savedChildren, grand_total_at_save: r.snapshot?.project_grand_total_at_save || 0, raw: r };
  };
  const leftSide = resolveSide(leftId);
  const rightSide = resolveSide(rightId);
  const sameSelected = leftId === rightId;
  const setSide = (which, id) => setComparingRound({
    left: which === "left" ? id : leftId,
    right: which === "right" ? id : rightId,
  });
  const leftRaw = leftSide.raw;
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="project-rounds-section">
      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><History size={16} /> Negotiation Rounds ({rounds.length})</h3>

      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Compare any two</div>
        <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="project-compare-picker">
          <select value={leftId} onChange={(e) => setSide("left", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="project-compare-left">
            <option value={CURRENT_KEY}>Current (live)</option>
            {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
          </select>
          <span className="text-slate-500 text-xs">vs</span>
          <select value={rightId} onChange={(e) => setSide("right", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="project-compare-right">
            <option value={CURRENT_KEY}>Current (live)</option>
            {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
          </select>
          {sameSelected && <span className="text-[11px] text-amber-600">Pick two different sides</span>}
        </div>
        {!sameSelected && (
          <ProjectCompareView
            round={{
              round_id: leftId,
              round_label: leftSide.label,
              round_date: leftRaw?.round_date || new Date().toISOString(),
              notes: leftRaw?.notes || "",
              created_by_name: leftRaw?.created_by_name || "",
              snapshot: {
                project: leftRaw?.snapshot?.project || project,
                children: (leftRaw?.snapshot?.children) || (leftSide.children.map((c) => ({ npd_id: c.npd_id, snapshot: c, grand_total_at_save: Number(c.grand_total) || 0 }))),
                project_grand_total_at_save: leftSide.grand_total_at_save,
              },
            }}
            currentChildren={rightSide.children}
            rightLabel={rightSide.label}
          />
        )}
      </div>

      <div className="space-y-2">
        {rounds.map((r) => {
          const isShortcut = leftId === r.round_id && rightId === CURRENT_KEY;
          const heroTotal = r.snapshot?.project_grand_total_at_save || 0;
          return (
            <div key={r.round_id} className={`border rounded-md p-3 ${isShortcut ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="min-w-0 flex items-center gap-2 flex-wrap">
                  <span className="inline-block text-xs font-bold bg-sky-100 text-sky-700 px-2 py-0.5 rounded">{r.round_label}</span>
                  <span className="text-xs text-slate-500">{new Date(r.round_date).toLocaleString("en-IN")}</span>
                  {r.created_by_name && <span className="text-xs text-slate-400">· by {r.created_by_name}</span>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-sm font-mono font-semibold text-slate-900">{formatINR(heroTotal)}</div>
                  <button
                    onClick={() => setComparingRound({ left: r.round_id, right: CURRENT_KEY })}
                    className={`text-xs px-2 py-1 rounded border whitespace-nowrap ${isShortcut ? "bg-sky-600 text-white border-sky-600" : "border-sky-500 text-sky-700 hover:bg-sky-50"}`}
                    data-testid={`project-compare-round-${r.round_label}`}
                    title="Quick compare this round vs Current"
                  >
                    vs Current
                  </button>
                </div>
              </div>
              {r.notes && <div className="text-xs text-slate-600 mt-1">{r.notes}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Project-level machine requirement — whole machines needed per tonnage across all parts
// (shared capacity: hours summed across parts, then rounded up).
function ProjectMachineRollup({ mr }) {
  const rows = mr?.rows || [];
  if (rows.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4" data-testid="project-machine-rollup">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Machine Requirement (whole project)</h2>
        <span className="text-xs text-slate-500">Total <strong className="text-indigo-700">{Number(mr.total_machines).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</strong> machine-load</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2 font-medium">Tonnage</th>
              <th className="text-left px-5 py-2 font-medium">Type</th>
              <th className="text-right px-5 py-2 font-medium">Hrs/mo (all parts)</th>
              <th className="text-right px-5 py-2 font-medium">Capacity</th>
              <th className="text-right px-5 py-2 font-medium">Machines (load)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.machine} className="hover:bg-slate-50">
                <td className="px-5 py-2 font-medium text-slate-800">{r.machine}</td>
                <td className="px-5 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">{MACHINE_TYPE_DISPLAY[r.type] || r.type}</span></td>
                <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(r.hours).toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                <td className="px-5 py-2 text-right font-mono text-slate-400">{r.capacity == null ? "—" : `${r.capacity} h/mo`}</td>
                <td className="px-5 py-2 text-right font-mono font-bold text-indigo-700">{Number(r.machines).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-semibold">
              <td className="px-5 py-2 text-slate-800" colSpan={2}>Total</td>
              <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(mr.total_hours).toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
              <td className="px-5 py-2"></td>
              <td className="px-5 py-2 text-right font-mono font-bold text-indigo-700">{Number(mr.total_machines).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-5 py-2 text-[10px] text-slate-400 border-t border-slate-100">
        Hours are summed across every part on each machine (shared capacity). "Machines (load)" is fractional — not rounded up. Press = 26d×10h = 260 h/mo · CNC/VMC = 26d×20h = 520 h/mo.
      </div>
    </div>
  );
}
